import React, { useState } from 'react'
import { mockVendors } from '../data/mockVendors'

interface ReviewFormData {
  vendorId: string
  rating: number
  title: string
  content: string
  pros: string[]
  cons: string[]
  wouldRecommend: boolean
  productsPurchased: string[]
  userName: string
}

interface ReviewFormProps {
  onSubmit: (review: ReviewFormData) => void
  onCancel: () => void
}

export default function ReviewForm({ onSubmit, onCancel }: ReviewFormProps) {
  const [formData, setFormData] = useState({
    vendorId: '',
    rating: 5,
    title: '',
    content: '',
    pros: [''],
    cons: [''],
    wouldRecommend: true,
    productsPurchased: [''],
    userName: ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    const cleanedData = {
      ...formData,
      pros: formData.pros.filter(p => p.trim() !== ''),
      cons: formData.cons.filter(c => c.trim() !== ''),
      productsPurchased: formData.productsPurchased.filter(p => p.trim() !== '')
    }

    onSubmit(cleanedData)
  }

  const addField = (field: 'pros' | 'cons' | 'productsPurchased') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }))
  }

  const removeField = (field: 'pros' | 'cons' | 'productsPurchased', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const updateField = (field: 'pros' | 'cons' | 'productsPurchased', index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }))
  }

  return (
    <div className="review-form-overlay">
      <form className="review-form" onSubmit={handleSubmit}>
        <div className="form-header">
          <h2>Write a Review</h2>
          <button type="button" onClick={onCancel} className="close-btn">×</button>
        </div>

        <div className="form-group">
          <label htmlFor="vendor">Vendor *</label>
          <select
            id="vendor"
            value={formData.vendorId}
            onChange={(e) => setFormData(prev => ({ ...prev, vendorId: e.target.value }))}
            required
          >
            <option value="">Select a vendor</option>
            {mockVendors.map(vendor => (
              <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="userName">Your Name *</label>
          <input
            type="text"
            id="userName"
            value={formData.userName}
            onChange={(e) => setFormData(prev => ({ ...prev, userName: e.target.value }))}
            required
            placeholder="Enter your name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="rating">Rating *</label>
          <div className="rating-input">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                type="button"
                className={`star-btn ${star <= formData.rating ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
              >
                ★
              </button>
            ))}
            <span className="rating-text">({formData.rating}/5)</span>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="title">Review Title *</label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            required
            placeholder="Summarize your experience"
          />
        </div>

        <div className="form-group">
          <label htmlFor="content">Review Content *</label>
          <textarea
            id="content"
            value={formData.content}
            onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
            required
            placeholder="Share your detailed experience with this vendor..."
            rows={5}
          />
        </div>

        <div className="form-group">
          <label>Products Purchased</label>
          {formData.productsPurchased.map((product, index) => (
            <div key={index} className="dynamic-field">
              <input
                type="text"
                value={product}
                onChange={(e) => updateField('productsPurchased', index, e.target.value)}
                placeholder="Product name and quantity"
              />
              {formData.productsPurchased.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeField('productsPurchased', index)}
                  className="remove-field-btn"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => addField('productsPurchased')}
            className="add-field-btn"
          >
            + Add Product
          </button>
        </div>

        <div className="form-group">
          <label>Pros</label>
          {formData.pros.map((pro, index) => (
            <div key={index} className="dynamic-field">
              <input
                type="text"
                value={pro}
                onChange={(e) => updateField('pros', index, e.target.value)}
                placeholder="What did you like?"
              />
              {formData.pros.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeField('pros', index)}
                  className="remove-field-btn"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => addField('pros')}
            className="add-field-btn"
          >
            + Add Pro
          </button>
        </div>

        <div className="form-group">
          <label>Cons</label>
          {formData.cons.map((con, index) => (
            <div key={index} className="dynamic-field">
              <input
                type="text"
                value={con}
                onChange={(e) => updateField('cons', index, e.target.value)}
                placeholder="What could be improved?"
              />
              {formData.cons.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeField('cons', index)}
                  className="remove-field-btn"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => addField('cons')}
            className="add-field-btn"
          >
            + Add Con
          </button>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.wouldRecommend}
              onChange={(e) => setFormData(prev => ({ ...prev, wouldRecommend: e.target.checked }))}
            />
            I would recommend this vendor to others
          </label>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
          <button type="submit" className="submit-btn">
            Submit Review
          </button>
        </div>
      </form>
    </div>
  )
}